import './style/exportInvoices.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    DvrdSelect,
    Loader,
    SelectItemShape
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ExportProcess from '../../../models/exportProcessModel';
import {BolmateButton, BolmateSelect, DvrdDatePicker} from '../../controls';
import {
    AccessKey,
    CustomerType,
    ExportNameAttr,
    ExportProcessStatus,
    Fulfilment,
    InvoiceExportType
} from '../../../util/interfaces';
import CountryField from '../../controls/country/field/countryField';
import classNames from 'classnames';
import useRight from '../../misc/useRight';
import InfoBanner from '../../misc/infoBanner';

interface Props {
    onChange: ChangeKeyFunction;
    onDownloadProcess: (process: ExportProcess) => MouseEventHandler;
    onSubmitProcess: MouseEventHandler;
    onChangeCountry: ChangeFunction<string>;
    onChangeNameAttr: ChangeFunction<ExportNameAttr>;
    onChangeReverseCharge: ChangeFunction;
    onChangeCustomerType: ChangeFunction;
    onClickFields: MouseEventHandler;
    onChangeFulfilment: ChangeFunction<Fulfilment>;
    exportProcess: ExportProcess;
    exportProcesses: ExportProcess[];
    loadingCount: boolean;
    loadingProcesses: boolean;
    loadingSubmit: boolean;
    invoiceCount: number;
    country: string;
    fulfilment: Fulfilment;
    nameAttr: ExportNameAttr;
    reverseChargeOnly: boolean;
    customerType: CustomerType;
    selectedFieldsCount: number;
}

export default function ExportInvoices(props: Props) {
    const {t} = useTranslation();
    const typeItems: SelectItemShape[] = [
        {label: t('settings.export.typeCsv', 'CSV (Excel)'), value: InvoiceExportType.CSV},
        {label: t('settings.export.typeCsvCustom', 'CSV (aangepast)'), value: InvoiceExportType.CSV_CUSTOM},
        {label: t('settings.export.typeUbl', 'UBL (boekhouding standaard)'), value: InvoiceExportType.UBL},
        {label: t('settings.export.typePdf', 'PDF'), value: InvoiceExportType.PDF},
        {label: t('settings.export.typeCombinedPdf', 'Gecombineerde PDF'), value: InvoiceExportType.COMBINED_PDF}
    ];

    const nameAttrItems: SelectItemShape[] = [
        {label: t('settings.export.nameOrderId', 'Ordernummer (Bol)'), value: ExportNameAttr.ORDER_ID},
        {label: t('settings.export.nameInvoiceNumber', 'Factuurnummer'), value: ExportNameAttr.INVOICE_NUMBER}
    ];

    const customerItems: SelectItemShape[] = [
        {label: t('settings.export.customerAll', 'Alle klanten'), value: CustomerType.BOTH},
        {label: t('settings.export.customerBusiness', 'Zakelijke klanten'), value: CustomerType.BUSINESS},
        {label: t('settings.export.customerPrivate', 'Particuliere klanten'), value: CustomerType.PRIVATE}
    ];

    const fulfilmentItems: SelectItemShape[] = [
        {label: t('settings.export.fulfilmentBoth', 'Iedere verzending'), value: Fulfilment.BOTH},
        {label: t('settings.export.fulfilmentFbr', 'Eigen verzending'), value: Fulfilment.FBR},
        {label: t('settings.export.fulfilmentFbb', 'LVB'), value: Fulfilment.FBB}
    ];

    const [canExport, canDownload] = useRight(AccessKey.EXPORT_SETTINGS_EXPORT, AccessKey.EXPORT_SETTINGS_DOWNLOAD);

    const {
        exportProcess,
        exportProcesses,
        loadingCount,
        loadingProcesses,
        onChange,
        onDownloadProcess,
        onSubmitProcess,
        invoiceCount,
        loadingSubmit,
        country,
        onChangeCountry,
        onChangeNameAttr,
        nameAttr,
        onChangeReverseCharge,
        reverseChargeOnly,
        customerType,
        onChangeCustomerType,
        selectedFieldsCount,
        onClickFields,
        onChangeFulfilment,
        fulfilment
    } = props;

    const countLabel = t('settings.export.invoiceCount', '{{count}} {{label}} geselecteerd', {
        count: invoiceCount,
        label: invoiceCount === 1 ? t('settings.export.invoice', 'factuur') : t('settings.export.invoices', 'facturen')
    });
    const [filtersOpen, setFiltersOpen] = useState(canExport);
    const submitDisabled = loadingCount || !exportProcess.fromDate || !exportProcess.toDate;

    function onToggleFilters() {
        setFiltersOpen(!filtersOpen);
    }

    function onChangeDate(key: string) {
        return function (value: IDate) {
            onChange(key)(value.format('YYYY-MM-DD'));
        };
    }

    function renderCreate() {
        const nameAttrVisible = [InvoiceExportType.PDF, InvoiceExportType.UBL].includes(exportProcess.exportType);

        return (
            <div className='block create'>
                <Loader active={loadingSubmit} />
                <div className='header-container'>
                    <h3 className='block-title'>{t('settings.export.exportTitle', 'Exporteren')}</h3>
                    <label className='block-sub'>
                        {t(
                            'settings.export.exportSub',
                            'Exporteren van de facturen of gegevens van een bepaalde periode.'
                        )}
                    </label>
                </div>

                <div className='type-container'>
                    <BolmateSelect
                        onChange={onChange('export_type')}
                        value={exportProcess.exportType}
                        items={typeItems}
                        label={t('settings.export.fileType', 'Bestandstype')}
                        disabled={loadingCount || !canExport}
                    />

                    {exportProcess.exportType === InvoiceExportType.COMBINED_PDF && (
                        <label className='combine-info'>
                            <AwesomeIcon name='info-circle' className='info' />{' '}
                            {t(
                                'settings.export.combinedInfo',
                                'Alle factuur- en BTW totalen worden bij elkaar opgeteld en op één factuur gezet.'
                            )}
                        </label>
                    )}

                    {nameAttrVisible && (
                        <BolmateSelect
                            value={nameAttr}
                            onChange={onChangeNameAttr}
                            items={nameAttrItems}
                            label={t('settings.export.filenameBase', 'Bestandsnaam baseren op')}
                            disabled={loadingCount || !canExport}
                        />
                    )}
                </div>

                {exportProcess.exportType === InvoiceExportType.CSV_CUSTOM && renderFieldsSelection()}
                {renderFilters()}

                <div className='btn-container'>
                    <BolmateButton
                        onClick={onSubmitProcess}
                        label={t('settings.export.create', 'Aanmaken')}
                        disabled={submitDisabled || !canExport}
                    />
                    <label className='invoice-count'>{countLabel}</label>
                </div>

                <div className='disclaimer'>
                    <label>
                        {t(
                            'settings.export.disclaimer1',
                            'Het aanmaken van het bestand kan even duren. Zodra deze klaar is, staat de download hieronder klaar. Je kunt ondertussen deze pagina verlaten.'
                        )}
                    </label>
                    <label className='italic'>
                        {t('settings.export.disclaimer2', 'Gebruik van gegevens op eigen risico.')}
                    </label>
                </div>
            </div>
        );
    }

    function renderFieldsSelection() {
        const fieldLabel =
            selectedFieldsCount === 1 ? t('settings.export.field', 'veld') : t('settings.export.fields', 'velden');

        return (
            <div className='fields-selection-container'>
                <label className='fields-count'>
                    {t('settings.export.fieldsSelected', '{{count}} {{label}} geselecteerd', {
                        count: selectedFieldsCount,
                        label: fieldLabel
                    })}
                </label>
                <BolmateButton
                    onClick={onClickFields}
                    label={t('settings.export.chooseFields', 'Velden kiezen')}
                    disabled={loadingSubmit || loadingCount || !canExport}
                />
            </div>
        );
    }

    function renderFilters() {
        return (
            <div className={classNames('filters-container', !filtersOpen && 'closed')}>
                <div className='filter-header-container' onClick={canExport ? onToggleFilters : undefined}>
                    <div className='line' />
                    <label className='filter-header-title'>{t('settings.export.filters', 'Filters')}</label>
                    <AwesomeIcon name='chevron-down' className='caret' />
                </div>

                <div className='filters'>
                    <div className='row'>
                        <label className='period-label'>{t('settings.export.period', 'Periode')}</label>
                        <div className='period-container'>
                            <DvrdDatePicker
                                onChange={onChangeDate('from_date')}
                                value={exportProcess.fromDateMoment}
                                label={t('settings.export.from', 'Vanaf')}
                                placeholder={t('settings.export.datePlaceholder', 'dd/mm/jjjj')}
                                disabled={loadingCount || !canExport}
                                dateFormat='DD/MM/YYYY'
                                closeOnChange
                            />
                            <div className='center-container'>
                                <span className='sep'>-</span>
                            </div>
                            <DvrdDatePicker
                                onChange={onChangeDate('to_date')}
                                value={exportProcess.toDateMoment?.clone() ?? null}
                                label={t('settings.export.to', 'Tot en met')}
                                placeholder={t('settings.export.datePlaceholder', 'dd/mm/jjjj')}
                                disabled={loadingCount || !canExport}
                                dateFormat='DD/MM/YYYY'
                                closeOnChange
                            />
                        </div>
                    </div>

                    <div className='row-2'>
                        <div className='filter-container'>
                            <CountryField
                                onChange={onChangeCountry}
                                value={country ?? ''}
                                label={t('settings.export.country', 'Land')}
                                disabled={!canExport}
                                withAll
                                labelAsValue
                            />
                        </div>
                        <div className='filter-container'>
                            <Checkbox
                                checked={reverseChargeOnly}
                                onCheck={onChangeReverseCharge}
                                label={t('settings.export.reverseChargeOnly', 'Alleen verlegde BTW')}
                                className='row-check'
                                disabled={!canExport}
                            />
                        </div>
                    </div>

                    <div className='row js'>
                        <DvrdSelect
                            value={customerType}
                            onChange={onChangeCustomerType}
                            label={t('settings.export.customerType', 'Type klant')}
                            items={customerItems}
                            disabled={!canExport}
                        />
                    </div>

                    <div className='row js'>
                        <DvrdSelect
                            value={fulfilment}
                            onChange={onChangeFulfilment}
                            label={t('settings.export.fulfilmentType', 'Type verzending')}
                            items={fulfilmentItems}
                            disabled={!canExport}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderProcesses() {
        return (
            <div className='block processes'>
                <Loader active={loadingProcesses} />
                <div className='header-container'>
                    <h3 className='block-title'>{t('settings.export.downloads', 'Downloads')}</h3>
                    <label className='block-sub'>
                        {t('settings.export.downloadsSub', 'Download de aangemaakte export hieronder.')}
                    </label>
                </div>

                <div className='process-list'>
                    <div className='row head'>
                        <label>{t('settings.export.colRequestDate', 'AANVRAAG DATUM')}</label>
                        <label>{t('settings.export.colPeriod', 'PERIODE')}</label>
                        <label>{t('settings.export.colFileType', 'BESTANDSTYPE')}</label>
                        <label>{t('settings.export.colAction', 'ACTIE')}</label>
                    </div>

                    {exportProcesses.map(process => (
                        <div className='row' key={process.id}>
                            <label>{process.createdAtMoment.format('DD/MM/YYYY')}</label>
                            <label>{process.periodLabel}</label>
                            <label>{process.translatedExportType}</label>
                            {renderProcessAction(process)}
                        </div>
                    ))}
                    {exportProcesses.length === 0 && (
                        <label className='empty-label'>
                            {t('settings.export.noFiles', '- nog geen bestanden aangemaakt -')}
                        </label>
                    )}
                </div>
            </div>
        );
    }

    function renderProcessAction(process: ExportProcess) {
        if (process.status === ExportProcessStatus.EXPORTING) {
            return (
                <label className='exporting'>
                    <Loader className='status-loader' size='1rem' thickness='.2rem' active disableBackground />
                    {t('settings.export.exporting', 'Bezig met exporteren...')}
                </label>
            );
        }

        if (process.status === ExportProcessStatus.ERROR) {
            const error = process.getFlex('error');
            return (
                <label className='error'>
                    {t('settings.export.error', 'Fout')}
                    {error ? ` - ${error}` : ''}
                </label>
            );
        }

        return (
            <label
                className={classNames('download', !canDownload && 'cursor-default')}
                onClick={canDownload ? onDownloadProcess(process) : undefined}
            >
                {canDownload ? t('settings.export.download', 'Downloaden') : t('settings.export.ready', 'Gereed')}
            </label>
        );
    }

    return (
        <div className='export-invoices'>
            <InfoBanner
                featureKey='export_invoices'
                variant='info'
                title={t('settings.export.bannerTitle', 'Facturen exporteren')}
                showForNewUsersOnly={true}
            >
                {t('settings.export.bannerBody', 'Exporteer jouw facturen en factuurgegevens.')}
            </InfoBanner>
            {renderCreate()}
            {renderProcesses()}
        </div>
    );
}
